import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { roleHome } from "@/utils/roles";
import { formatGNF, imgUrl } from "@/utils/format";
import { SpicyBadge } from "@/components/SpicyBadge";
import DishOptionsModal from "@/components/DishOptionsModal";

export default function Cart() {
  const { t } = useTranslation();
  const { cart, updateQuantity, removeItem, updateLine, subtotal, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editLine, setEditLine] = useState(null); // ligne du panier en cours d'édition
  const deliveryFee = cart.restaurant?.delivery_fee || 0;
  const total = subtotal + deliveryFee;

  // Cart is a client/visitor feature only — partners and admins don't order
  if (user && user.role !== "client") return <Navigate to={roleHome(user.role)} replace />;

  if (count === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <ShoppingBag className="w-12 h-12 mx-auto text-mute mb-4" />
        <h1 className="font-display text-3xl font-semibold">{t("cart.empty")}</h1>
        <p className="text-mute mt-2 mb-6">{t("cart.emptyText")}</p>
        <Link to="/restaurants" className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 rounded-full font-semibold">
          {t("cart.browseRestaurants")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-2">{t("cart.title")}</h1>
      <p className="text-mute mb-8">{t("restaurantDetail.menu")} · <span className="text-ink font-semibold">{cart.restaurant?.name}</span></p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-3">
          {cart.items.map((i) => {
            const customizable = i.ingredients?.some((x) => x.type !== "fixed") || i.cooking_choices?.length > 0;
            const unitTotal = i.price + (i.options_total || 0);
            return (
              <div key={i.line_id} data-testid={`cart-item-${i.dish_id}`} className="bg-white border border-sand rounded-2xl p-3 sm:p-4 flex flex-wrap sm:flex-nowrap gap-3 sm:gap-4 items-center">
                <img src={imgUrl(i.image)} alt={i.name} className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover bg-elevated shrink-0" />
                <div
                  className={`flex-1 min-w-0 ${customizable ? "cursor-pointer" : ""}`}
                  onClick={() => customizable && setEditLine(i)}
                  data-testid={`cart-line-${i.line_id}`}
                >
                  <div className="font-display text-lg font-semibold truncate flex items-center gap-2">
                    {i.name} <SpicyBadge level={i.spicy_level} testId={`cart-spicy-${i.dish_id}`} />
                    {customizable && <Pencil className="w-3.5 h-3.5 text-mute shrink-0" />}
                  </div>
                  {i.selected_options?.length > 0 && (
                    <div className="text-xs text-mute truncate" data-testid={`cart-options-${i.line_id}`}>
                      {i.selected_options.map((o) => o.price_delta > 0 ? `${o.name} (+${formatGNF(o.price_delta)})` : o.name).join(" · ")}
                    </div>
                  )}
                  {i.note && <div className="text-xs text-mute italic truncate" data-testid={`cart-note-${i.line_id}`}>« {i.note} »</div>}
                  <div className="text-sm text-mute">{formatGNF(unitTotal)}</div>
                </div>
                <div className="w-full sm:w-auto order-3 sm:order-none flex items-center justify-between sm:justify-end gap-2 sm:gap-3 pt-2 sm:pt-0 border-t border-sand/60 sm:border-0">
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(i.line_id, i.quantity - 1)} data-testid={`cart-qty-minus-${i.line_id}`} className="rounded-full bg-elevated p-2"><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center font-semibold">{i.quantity}</span>
                    <button onClick={() => updateQuantity(i.line_id, i.quantity + 1)} data-testid={`cart-qty-plus-${i.line_id}`} className="rounded-full bg-elevated p-2"><Plus className="w-3 h-3" /></button>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="text-right font-display font-semibold whitespace-nowrap sm:min-w-[6rem]" data-testid={`cart-line-total-${i.line_id}`}>{formatGNF(unitTotal * i.quantity)}</div>
                    <button onClick={() => removeItem(i.line_id)} data-testid={`cart-remove-${i.line_id}`} className="rounded-full p-2 hover:bg-elevated text-brand-red shrink-0" aria-label={t("cart.remove")}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <aside className="bg-white border border-sand rounded-2xl p-6 h-fit sticky top-24">
          <h3 className="font-display text-xl font-semibold mb-4">{t("cart.total")}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-mute">{t("cart.subtotal")}</span><span>{formatGNF(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-mute">{t("cart.delivery")}</span><span>{formatGNF(deliveryFee)}</span></div>
            <div className="border-t border-sand pt-3 mt-3 flex justify-between font-display text-lg font-semibold">
              <span>{t("cart.total")}</span><span>{formatGNF(total)}</span>
            </div>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            data-testid="go-to-checkout"
            className="w-full mt-5 rounded-full bg-brand-red hover:bg-[#C81F2D] text-white py-3 font-semibold inline-flex items-center justify-center gap-2"
          >
            {t("cart.checkout")} <ArrowRight className="w-4 h-4" />
          </button>
        </aside>
      </div>

      <DishOptionsModal
        open={!!editLine}
        dish={editLine}
        initial={editLine ? { selected_options: editLine.selected_options, quantity: editLine.quantity } : null}
        confirmLabel="Mettre à jour"
        onClose={() => setEditLine(null)}
        onConfirm={(custom) => {
          updateLine(editLine.line_id, custom);
          setEditLine(null);
        }}
      />
    </div>
  );
}
